import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3, CreditCard, User, X, Music2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
const logo = '/images/landing/Pravos_Lockup_Horizontal_Dark_Glow--no-bg.webp';
import { UpgradeModal } from '@/components/upgrade/UpgradeModal';
import { useSubscription } from '@/hooks/useSubscription';
import { FocusIcon } from '@/components/icons/FocusIcon';

interface AppNavProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

export function AppNav({ mobileMenuOpen, setMobileMenuOpen }: AppNavProps) {
  const location = useLocation();
  const { hasActiveSubscription } = useSubscription();
  const [mobileVolume, setMobileVolume] = useState(() => {
    const saved = localStorage.getItem('audioVolume');
    return saved ? parseFloat(saved) : 0.7;
  });
  const [isMobileVolumeActive, setIsMobileVolumeActive] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const isActive = (path: string): boolean => {
    return location.pathname === path;
  };

  const navItems = [
    {
      path: '/app',
      label: 'Focus',
      icon: FocusIcon,
      type: 'link' as const,
    },
    {
      path: '/app/music',
      label: 'Music',
      icon: Music2,
      type: 'link' as const,
    },
    {
      path: '/app/insights',
      label: 'Insights',
      icon: BarChart3,
      type: 'link' as const,
    },
  ];

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setMobileVolume(newVolume);
    localStorage.setItem('audioVolume', newVolume.toString());
    // Dispatch event so AudioPlaybar can listen
    window.dispatchEvent(
      new CustomEvent('volumeChange', { detail: newVolume })
    );
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-bg-primary/50 h-[70px] md:h-[80px] lg:h-[90px] border-b border-white/5">
        <div className="max-w-[1728px] mx-auto h-full flex items-center justify-between px-4 md:px-6 lg:px-8 relative">
          {/* Logo + Wordmark + Beta Pill */}
          <div className="flex items-center gap-2 z-10">
            <Link
              to="/app"
              className="flex items-center group"
              onClick={closeMobileMenu}
            >
              <div className="relative w-[134px] h-[42px] md:w-[150px] md:h-[48px] lg:w-[165px] lg:h-[52px]">
                <img
                  src={logo}
                  alt="Pravos Logo"
                  className="relative w-full h-full object-contain"
                />
              </div>
            </Link>

            {/* BETA Label */}
            <span className="font-sans text-[9px] md:text-[10px] font-bold tracking-[0.15em] md:tracking-[0.2em] text-transparent bg-clip-text bg-gradient-to-r from-[#8B6FCB] via-[#9D7FD6] to-[#C98AA6] translate-y-[-0.5em] translate-x-[-1em] inline-block">
              BETA
            </span>
          </div>

          {/* Desktop Center Navigation - Centered in viewport */}
          <div className="hidden nav:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
            {navItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="group relative flex flex-col items-start"
                >
                  <span
                    className={`flex items-center gap-2 pb-2 transition-colors ${
                      active
                        ? 'text-text-primary'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-sans text-body tracking-[0.08em]">
                      {item.label}
                    </span>
                  </span>
                  <span
                    className={`absolute left-0 right-0 -bottom-0.5 h-[2px] rounded-full bg-brand-purple transition-all ${
                      active
                        ? 'opacity-100 scale-x-100'
                        : 'opacity-0 scale-x-90 group-hover:opacity-60 group-hover:scale-x-100'
                    }`}
                  />
                </Link>
              );
            })}
          </div>

          {/* Right Side Actions - Always Visible */}
          <div className="flex items-center gap-3 lg:gap-4 z-10">
            {/* Upgrade Button - Hide for Pro users */}
            {!hasActiveSubscription && (
              <Button
                variant="pravos-primary"
                onClick={() => setIsUpgradeModalOpen(true)}
                className="h-[36px] md:h-[40px] lg:h-[42px] px-4 md:px-5 lg:px-6 shadow-glow-sm flex items-center gap-2 tracking-[0.3em] md:tracking-[0.35em] lg:tracking-[0.4em] text-[11px] md:text-[12px] lg:text-[13px]"
              >
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span className="font-sans font-semibold leading-none">
                  Upgrade
                </span>
              </Button>
            )}

            {/* Profile Icon */}
            <Link to="/app/profile">
              <div className="relative inline-block">
                <button
                  className={`p-2 rounded-full transition-all ${
                    isActive('/app/profile')
                      ? 'bg-button-gradient text-white shadow-glow-sm'
                      : hasActiveSubscription
                        ? 'bg-glass-dark text-text-primary'
                        : 'bg-glass-dark text-text-secondary hover:text-text-primary hover:bg-glass-dark/80 hover:border-2 hover:border-brand-purple'
                  }`}
                  style={
                    hasActiveSubscription && !isActive('/app/profile')
                      ? {
                          backgroundOrigin: 'border-box',
                          backgroundClip: 'padding-box, border-box',
                          border: '2px solid transparent',
                          backgroundImage:
                            'linear-gradient(rgba(13, 13, 23, 1), rgba(13, 13, 23, 1)), linear-gradient(135deg, rgba(139, 111, 203, 0.82) 0%, rgba(157, 127, 214, 0.9) 45%, rgba(201, 138, 166, 0.88) 100%)',
                        }
                      : undefined
                  }
                >
                  <User className="w-5 h-5" />
                </button>
                {/* Pro Badge */}
                {hasActiveSubscription && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="absolute top-[-8px] left-[6px] -translate-x-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider bg-button-gradient text-white shadow-glow-sm"
                    style={{
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    }}
                  >
                    PRO
                  </motion.div>
                )}
              </div>
            </Link>
          </div>

          {/* Mobile Hamburger Menu Button with Label - Hidden, now in AudioPlaybar */}
          {/* Keeping this commented for tablet/desktop if needed */}
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm nav:hidden"
            onClick={closeMobileMenu}
          >
            {/* Mobile Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute right-0 top-0 bottom-0 w-[280px] bg-glass-dark border-l border-glass-border shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex flex-col h-full p-6 relative">
                {/* Navigation Links */}
                <div className="space-y-2 mb-8 mt-[70px]">
                  {navItems.map(item => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={closeMobileMenu}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                          active
                            ? 'bg-brand-purple/20 text-text-primary border border-brand-purple/30'
                            : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-sans text-[16px] tracking-wide">
                          {item.label}
                        </span>
                      </Link>
                    );
                  })}
                </div>

                {/* Mobile Actions */}
                <div className="space-y-4 mt-auto mb-20">
                  {/* Volume Slider */}
                  <div className="px-4 py-3 bg-glass-dark border border-glass-border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-sans text-[14px] text-text-secondary">
                        Volume
                      </span>
                      <span className="font-mono text-[14px] text-text-primary">
                        {Math.round(mobileVolume * 100)}%
                      </span>
                    </div>
                    <div className="relative h-4 flex items-center">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={mobileVolume}
                        onChange={handleMobileVolumeChange}
                        onMouseDown={() => setIsMobileVolumeActive(true)}
                        onMouseUp={() => setIsMobileVolumeActive(false)}
                        onTouchStart={() => setIsMobileVolumeActive(true)}
                        onTouchEnd={() => setIsMobileVolumeActive(false)}
                        className="w-full h-4 bg-transparent rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer relative z-10"
                      />
                      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 bg-white/10 rounded-full pointer-events-none" />
                      <div
                        className={`absolute left-0 top-1/2 -translate-y-1/2 h-2 rounded-full pointer-events-none transition-all duration-100 ${
                          isMobileVolumeActive
                            ? 'bg-gradient-to-r from-[#C98AA6] to-[#8B6FCB]'
                            : 'bg-text-secondary/60'
                        }`}
                        style={{ width: `${mobileVolume * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Close Button - Bottom Right */}
                <button
                  onClick={closeMobileMenu}
                  className="absolute bottom-6 right-6 p-3 rounded-full bg-glass-dark border border-glass-border text-text-secondary hover:text-text-primary hover:border-brand-purple/30 transition-all"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
      />
    </>
  );
}
